import React from 'react';
import { getProjectStats, getScoreTypeStats, getAudioRequirements } from '../data/mockProject';

const Sidebar = ({ project, isOpen, onToggle, activeTab, onTabClick, scoreTypes }) => {
  const stats = getProjectStats(project);
  const audioRequirements = getAudioRequirements(project);

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div className="sidebar-overlay" onClick={onToggle} />
      )}
      
      <aside className={`sidebar ${isOpen ? 'sidebar-open' : ''}`}>
        <div className="sidebar-header">
          <div className="sidebar-branding">
            <span className="sidebar-logo">AUDDICT</span>
            <span className="sidebar-label">Recording Portal</span>
          </div>
          <button className="sidebar-close" onClick={onToggle} aria-label="Close the navigation menu">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        <div className="sidebar-project">
          <p className="sidebar-project-label">Your recording project</p>
          <h2 className="sidebar-project-name">{project.project_name}</h2>
        </div>

        <div className="sidebar-stats">
          <div className="sidebar-stat">
            <span className="sidebar-stat-number">{stats.uploaded}</span>
            <span className="sidebar-stat-label">of {stats.total} recordings uploaded</span>
          </div>
          <div className="sidebar-stat">
            <span className="sidebar-stat-number sidebar-stat-approved">{stats.approved}</span>
            <span className="sidebar-stat-label">recordings approved so far</span>
          </div>
          {stats.needsAttention > 0 && (
            <div className="sidebar-stat">
              <span className="sidebar-stat-number sidebar-stat-attention">{stats.needsAttention}</span>
              <span className="sidebar-stat-label">recordings need your attention</span>
            </div>
          )}
        </div>

        <div className="sidebar-progress">
          <div className="sidebar-progress-bar">
            <div 
              className="sidebar-progress-approved" 
              style={{ width: `${(stats.approved / stats.total) * 100}%` }}
            />
            <div 
              className="sidebar-progress-pending" 
              style={{ 
                width: `${((stats.uploaded - stats.approved) / stats.total) * 100}%`,
                left: `${(stats.approved / stats.total) * 100}%`
              }}
            />
          </div>
        </div>

        {/* Audio format tip - shown once in sidebar */}
        <div className="sidebar-tip">
          <div className="sidebar-tip-icon">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="16" x2="12" y2="12" />
              <line x1="12" y1="8" x2="12.01" y2="8" />
            </svg>
          </div>
          <div className="sidebar-tip-content">
            <span className="sidebar-tip-label">Audio format required</span>
            <span className="sidebar-tip-value">{audioRequirements}</span>
          </div>
        </div>

        <nav className="sidebar-nav">
          <h3 className="sidebar-nav-title">Sections</h3>
          <ul className="sidebar-nav-list">
            {/* Introduction tab */}
            <li>
              <button 
                className={`sidebar-nav-item ${activeTab === 'introduction' ? 'active' : ''}`}
                onClick={() => onTabClick('introduction')}
              >
                <span className="nav-item-icon">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                    <polyline points="14 2 14 8 20 8" />
                    <line x1="16" y1="13" x2="8" y2="13" />
                    <line x1="16" y1="17" x2="8" y2="17" />
                    <polyline points="10 9 9 9 8 9" />
                  </svg>
                </span>
                <span className="nav-item-text">Introduction</span>
              </button>
            </li>

            {/* Score type tabs */}
            {scoreTypes.map(scoreType => {
              const typeStats = getScoreTypeStats(project, scoreType);
              const isComplete = typeStats.approved === typeStats.total;
              const hasIssues = typeStats.needsAttention > 0;
              
              return (
                <li key={scoreType}>
                  <button 
                    className={`sidebar-nav-item ${activeTab === scoreType ? 'active' : ''} ${isComplete ? 'complete' : ''} ${hasIssues ? 'has-issues' : ''}`}
                    onClick={() => onTabClick(scoreType)}
                  >
                    <span className="nav-item-icon">
                      {isComplete ? (
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                          <polyline points="22 4 12 14.01 9 11.01" />
                        </svg>
                      ) : hasIssues ? (
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <circle cx="12" cy="12" r="10" />
                          <line x1="12" y1="8" x2="12" y2="12" />
                          <line x1="12" y1="16" x2="12.01" y2="16" />
                        </svg>
                      ) : (
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <circle cx="12" cy="12" r="10" />
                        </svg>
                      )}
                    </span>
                    <span className="nav-item-text">{scoreType}</span>
                    <span className="nav-item-count">{typeStats.approved}/{typeStats.total}</span>
                  </button>
                </li>
              );
            })}
          </ul>
        </nav>

        <div className="sidebar-footer">
          <button className="sidebar-download-btn" disabled>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="7 10 12 15 17 10" />
              <line x1="12" y1="15" x2="12" y2="3" />
            </svg>
            Download a printable checklist
            <span className="coming-soon-tag">Coming soon</span>
          </button>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
